import { createFileRoute, useRouter } from "@tanstack/react-router";

import { BRAND } from "~/brand";
import { ErrorState } from "~/components/ErrorState";
import { PageTransition } from "~/components/PageTransition";
import { StepFlowLayout } from "~/components/layouts/StepFlowLayout";
import { Button } from "~/components/ui/button";
import { authClient } from "~/lib/auth";

/**
 * Landing page for a customer whose account has been deactivated.
 *
 * Deactivation deliberately preserves the login — a returning customer should be
 * able to sign in with their existing credentials rather than being forced
 * through a brand-new signup. But their old profile, chargers and payout details
 * must not be shown back to them: reactivation means fresh onboarding, and
 * whatever they enter next time is authoritative. So sign-in succeeds and lands
 * here.
 *
 * Lives in the un-guarded `(public)` group on purpose: putting it inside
 * `(authenticated)` would make the guard that redirects here loop.
 *
 * Layout follows the app's standalone-page pattern — StepFlowLayout shell, white
 * panel, ErrorState body — the same combination used for the unknown-provider and
 * failed-connection screens (see components/onboarding/ConnectAccountManual.tsx).
 * `showBack` is off because there is nowhere useful to go back to.
 */
export const Route = createFileRoute("/(public)/account-inactief")({
  component: AccountInactiveRoute,
});

function AccountInactiveRoute() {
  const router = useRouter();

  const handleSignOut = async () => {
    await authClient.signOut({
      fetchOptions: {
        onSuccess: async () => {
          await router.invalidate();
          router.navigate({ to: "/inloggen" });
        },
      },
    });
  };

  return (
    <StepFlowLayout showBack={false}>
      <div className="bg-brand-white mx-4 mb-12 flex w-full max-w-2xl flex-col gap-6 p-12 lg:pb-10">
        <ErrorState
          message={`Je account bij ${BRAND.name} is stopgezet. Je laadsessies worden niet meer uitgelezen en je ontvangt geen e-mails meer van ons. Wil je weer gebruikmaken van ${BRAND.name}? Neem dan contact met ons op. We zetten je account graag weer voor je klaar.`}
          retryLabel="Neem contact op"
          retryUrl="mailto:info@lekkerladen.com"
          showContactLink={false}
          title="Je account is niet actief"
        />

        {/* ErrorState has no slot for a second action, so the sign-out sits below
            it in the same visual language: a link-style button, matching the
            secondary action ErrorState renders for its own contact link. */}
        <PageTransition delay={200}>
          <div className="flex flex-col items-center">
            <Button onClick={handleSignOut} type="button" variant="link">
              Uitloggen
            </Button>
          </div>
        </PageTransition>
      </div>
    </StepFlowLayout>
  );
}
